import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { UploadCloud, X, Loader2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { DOC_SECTIONS } from './docCatalog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALL_TYPES = DOC_SECTIONS.flatMap((s) => s.docs.map((d) => ({ value: d.type, label: d.label })));

const SOURCES = [
  { value: 'baobab', label: 'Baobab (interne)' },
  { value: 'societe', label: 'Émis par la société cliente' },
  { value: 'terrain', label: 'Récupéré sur le terrain' },
];

const AddDocumentModal = ({ open, onOpenChange, operationId, defaultType = 'autre', initialFiles = [], onUploaded }) => {
  const [docType, setDocType] = useState(defaultType);
  const [source, setSource] = useState('baobab');
  const [comment, setComment] = useState('');
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setDocType(defaultType || 'autre');
      setFiles(initialFiles && initialFiles.length ? Array.from(initialFiles) : []);
      setSource('baobab');
      setComment('');
    }
  }, [open, defaultType]); // eslint-disable-line

  const onPick = (e) => setFiles((prev) => [...prev, ...Array.from(e.target.files || [])]);
  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!files.length) { toast.error('Sélectionnez au moins un fichier'); return; }
    setBusy(true);
    const srcLabel = SOURCES.find((s) => s.value === source)?.label || '';
    const notes = [srcLabel ? `Source: ${srcLabel}` : '', comment].filter(Boolean).join(' | ');
    let ok = 0;
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append('file', f);
        fd.append('documentType', docType);
        if (notes) fd.append('notes', notes);
        await axios.post(`${API}/operations/${operationId}/documents`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        ok += 1;
      } catch (e) {
        toast.error(e.response?.data?.detail || `Échec: ${f.name}`);
      }
    }
    setBusy(false);
    if (ok > 0) {
      toast.success(`${ok} document(s) ajouté(s) avec succès`);
      onUploaded?.();
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="add-document-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UploadCloud className="w-5 h-5 text-emerald-600" /> Ajouter un document</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="space-y-1.5">
            <Label>Type de document</Label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger data-testid="add-doc-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ALL_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div
            className="border-2 border-dashed border-gray-200 rounded-xl p-5 text-center cursor-pointer hover:border-emerald-300 hover:bg-emerald-50/30 transition-colors"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); setFiles((p) => [...p, ...Array.from(e.dataTransfer.files || [])]); }}
            data-testid="add-doc-dropzone"
          >
            <UploadCloud className="w-7 h-7 mx-auto text-emerald-500 mb-1.5" />
            <p className="text-sm text-gray-700">Cliquez ou glissez vos fichiers ici</p>
            <p className="text-xs text-gray-400 mt-0.5">PDF, JPG, PNG — plusieurs fichiers acceptés</p>
            <input ref={inputRef} type="file" multiple hidden onChange={onPick}
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" />
          </div>

          {files.length > 0 && (
            <div className="space-y-1.5 max-h-32 overflow-y-auto">
              {files.map((f, i) => (
                <div key={i} className="flex items-center justify-between text-sm bg-gray-50 rounded-lg px-3 py-1.5">
                  <span className="flex items-center gap-2 min-w-0"><FileText className="w-4 h-4 text-gray-400 shrink-0" /><span className="truncate">{f.name}</span></span>
                  <button onClick={() => removeFile(i)} className="text-gray-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 gap-3">
            <div className="space-y-1.5">
              <Label>Source du document</Label>
              <Select value={source} onValueChange={setSource}>
                <SelectTrigger data-testid="add-doc-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SOURCES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Commentaire (optionnel)</Label>
              <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder="Précisions…" data-testid="add-doc-comment" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Annuler</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-doc-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <UploadCloud className="w-4 h-4 mr-1.5" />}
            Ajouter le document
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddDocumentModal;
